import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Link } from "react-router-dom";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { platformFetch } from "@/services/http/platform-fetch";

/** The FR-7 wire row (`GET /api/platform/admin/tenants`). */
export type SchoolListItem = {
  id: string;
  slug: string;
  name: string;
  createdAt: string;
  memberCount: number;
  courseCount: number;
  setup: { done: number; total: number; missing: string[] };
};

type SchoolListPage = {
  data: SchoolListItem[];
  meta: { total: number; page: number; limit: number };
};

export const SCHOOL_LIST_QUERY_KEY = ["platform-schools"] as const;
const LIMIT = 25;

async function fetchSchools(q: string, page: number): Promise<SchoolListPage> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  params.set("page", String(page));
  params.set("limit", String(LIMIT));
  const res = await platformFetch(`/platform/admin/tenants?${params.toString()}`);
  if (!res.ok) throw await readApiErrorResponse(res);
  return (await res.json()) as SchoolListPage;
}

/**
 * MT-1 Plan 2 S13b (FR-7 UI): every school, newest first, with its counts and setup summary;
 * searched by address or name, 25 a page.
 */
export function SchoolList() {
  const t = useTranslations("platform.schools");
  const tRoot = useTranslations();
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: [...SCHOOL_LIST_QUERY_KEY, q, page],
    queryFn: () => fetchSchools(q, page),
    placeholderData: keepPreviousData,
  });
  const lastPage = query.data ? Math.max(1, Math.ceil(query.data.meta.total / LIMIT)) : 1;

  function search(e: React.FormEvent) {
    e.preventDefault();
    setQ(draft.trim());
    setPage(1);
  }

  return (
    <section
      className="mt-8 rounded-lg border border-[var(--border-strong)] p-4"
      aria-labelledby="school-list-title"
      data-testid="school-list"
    >
      <h2 id="school-list-title" className="text-lg font-semibold">
        {t("title")}
      </h2>
      <form role="search" onSubmit={search} className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <label htmlFor="school-list-search" className="mb-1 block text-sm">
            {t("searchLabel")}
          </label>
          <input
            id="school-list-search"
            data-testid="school-search"
            type="search"
            value={draft}
            maxLength={200}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-[var(--text-primary)]"
          />
        </div>
        <button
          type="submit"
          className="rounded-md border border-[var(--border-strong)] px-3 py-2 text-sm"
        >
          {t("searchButton")}
        </button>
      </form>

      {query.isPending && <p className="mt-3 text-sm text-[var(--text-muted)]">{t("loading")}</p>}
      {query.isError && (
        <p
          role="alert"
          className="mt-3 text-sm text-[var(--danger)]"
          data-testid="school-list-error"
        >
          {apiErrorText(query.error, tRoot, "platform.schools.loadFailed")}
        </p>
      )}
      {query.data && query.data.data.length === 0 && (
        <p className="mt-3 text-sm" data-testid="school-list-empty">
          {t("noMatch")}
        </p>
      )}
      {query.data && query.data.data.length > 0 && (
        <>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="school-table">
              <caption className="sr-only">{t("caption")}</caption>
              <thead>
                <tr>
                  <th scope="col" className="py-2 pr-3">
                    {t("columnName")}
                  </th>
                  <th scope="col" className="py-2 pr-3">
                    {t("columnAddress")}
                  </th>
                  <th scope="col" className="py-2 pr-3">
                    {t("columnMembers")}
                  </th>
                  <th scope="col" className="py-2 pr-3">
                    {t("columnCourses")}
                  </th>
                  <th scope="col" className="py-2">
                    {t("columnSetup")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data.data.map((school) => (
                  <tr
                    key={school.id}
                    className="border-t border-[var(--border-strong)]"
                    data-testid="school-row"
                  >
                    <th scope="row" className="py-2 pr-3 font-medium">
                      <Link
                        to={`/platform/admin/schools/${school.id}`}
                        className="underline"
                        data-testid="school-link"
                      >
                        {school.name}
                      </Link>
                    </th>
                    <td className="py-2 pr-3">/t/{school.slug}</td>
                    <td className="py-2 pr-3">{school.memberCount}</td>
                    <td className="py-2 pr-3">{school.courseCount}</td>
                    <td className="py-2">
                      {t("setupSummary", { done: school.setup.done, total: school.setup.total })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <nav aria-label={t("pagination")} className="mt-3 flex items-center gap-3 text-sm">
            <button
              type="button"
              aria-label={t("previousPage")}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-md border border-[var(--border-strong)] px-3 py-1 disabled:opacity-50"
            >
              ‹
            </button>
            <span>{t("pageOf", { page, pages: lastPage })}</span>
            <button
              type="button"
              aria-label={t("nextPage")}
              disabled={page >= lastPage}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-md border border-[var(--border-strong)] px-3 py-1 disabled:opacity-50"
            >
              ›
            </button>
          </nav>
        </>
      )}
    </section>
  );
}
